import type { CalendarSettings } from "@ai-admin/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { queryClient, trpc } from "../../app/trpc.js";
import { Button, Card, FormRow, Input, Notice, PageTitle, Spinner } from "../../ui/index.js";
import { useCabinet } from "../cabinet/index.js";

type NumKey = "slotStepMin" | "minLeadMin" | "horizonDays" | "lateCancelHours" | "lateArrivalToleranceMin" | "maxActiveBookingsPerPhone";
const FIELDS: NumKey[] = ["slotStepMin", "minLeadMin", "horizonDays", "lateCancelHours", "lateArrivalToleranceMin", "maxActiveBookingsPerPhone"];

/** Правила календаря салона: шаг сетки, запись не раньше чем, горизонт, поздняя отмена, опоздание, лимит записей на телефон. */
export function CalendarRulesPage() {
  const { t } = useTranslation();
  const cab = useCabinet();
  const q = useQuery(trpc.calendar.settings.get.queryOptions({ salonId: cab.salonId }));
  const [form, setForm] = useState<Record<NumKey, string> | null>(null);
  const save = useMutation(
    trpc.calendar.settings.update.mutationOptions({
      onSuccess: (data) => queryClient.setQueryData(trpc.calendar.settings.get.queryKey({ salonId: cab.salonId }), data),
    }),
  );
  useEffect(() => {
    if (q.data && !form) setForm(Object.fromEntries(FIELDS.map((k) => [k, String(q.data[k])])) as Record<NumKey, string>);
  }, [q.data, form]);

  if (q.isPending || !form) return <Spinner />;
  const patch = Object.fromEntries(FIELDS.map((k) => [k, Number(form[k])])) as Partial<CalendarSettings>;
  return (
    <div className="lg:max-w-3xl">
      <PageTitle>{t("rules.title")}</PageTitle>
      <Card className="space-y-4">
        {FIELDS.map((k) => (
          <FormRow key={k} label={t(`rules.${k}`)} htmlFor={k} hint={t(`rules.${k}Hint`)}>
            <Input
              id={k}
              inputMode="numeric"
              value={form[k]}
              onChange={(e) => {
                save.reset();
                setForm({ ...form, [k]: e.target.value.replace(/\D/g, "") });
              }}
            />
          </FormRow>
        ))}
      </Card>
      {save.isError && <Notice kind="error">{t("rules.invalid")}</Notice>}
      {save.isSuccess && <Notice kind="success">{t("settings.saved")}</Notice>}
      <Button className="mt-4 w-full lg:w-auto" disabled={save.isPending} onClick={() => save.mutate({ salonId: cab.salonId, patch })}>
        {t("settings.save")}
      </Button>
    </div>
  );
}
